import { useState } from "react";
import { useList, useCreate, useUpdate, useRemove } from "@/hooks/crud";
import { Modal } from "@/components/Modal";
import { TextField, TextAreaField } from "@/components/Fields";
import { Spinner, ErrorBanner, Empty } from "@/components/Feedback";
import { slugify } from "@/lib/format";
import type { Artist, Project } from "@/types/database";

type ProjectRow = Project & { artists?: { name: string } | null };

function ProjectForm({
  project,
  artists,
  onClose,
}: {
  project: Project | null;
  artists: Artist[];
  onClose: () => void;
}) {
  const create = useCreate("projects");
  const update = useUpdate("projects");
  const [name, setName] = useState(project?.name ?? "");
  const [slug, setSlug] = useState(project?.slug ?? "");
  const [artistId, setArtistId] = useState(
    project?.artist_id ? String(project.artist_id) : artists[0] ? String(artists[0].id) : "",
  );
  const [genre, setGenre] = useState(project?.genre_theme ?? "");
  const [tribute, setTribute] = useState(project?.tribute_of ?? "");
  const [description, setDescription] = useState(project?.description ?? "");
  const mutation = project ? update : create;

  async function save() {
    if (!name.trim() || !artistId) return;
    const body = {
      name: name.trim(),
      slug: (slug.trim() || slugify(name)).trim(),
      artist_id: Number(artistId),
      genre_theme: genre.trim() || null,
      tribute_of: tribute.trim() || null,
      description: description.trim() || null,
    };
    if (project) await update.mutateAsync({ id: project.id, body });
    else await create.mutateAsync(body);
    onClose();
  }

  return (
    <Modal
      title={project ? "Edit Project" : "New Project"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={save}
            disabled={mutation.isPending || !name.trim() || !artistId}
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      {mutation.error && <ErrorBanner error={mutation.error} />}
      <div className="field">
        <label>Artist *</label>
        <select value={artistId} onChange={(e) => setArtistId(e.target.value)}>
          <option value="">— select —</option>
          {artists.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>
      <TextField label="Name *" value={name} onChange={setName} />
      <TextField label="Slug" value={slug} onChange={setSlug} placeholder={slugify(name) || "auto"} />
      <div className="field-row">
        <TextField label="Genre / theme" value={genre} onChange={setGenre} />
        <TextField label="Tribute of" value={tribute} onChange={setTribute} />
      </div>
      <TextAreaField label="Description" value={description} onChange={setDescription} rows={3} />
    </Modal>
  );
}

export default function ProjectsList() {
  const { data, isLoading, error } = useList(
    "projects",
    "select=*,artists(name)&order=name",
  );
  const artistsQuery = useList("artists", "select=id,name&order=name");
  const remove = useRemove("projects");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const projects = (data?.rows ?? []) as ProjectRow[];
  const artists = (artistsQuery.data?.rows ?? []) as Artist[];

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Projects</h1>
          <div className="page-sub">{data ? `${data.total} sub-projects / series` : "—"}</div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCreating(true)}
          disabled={artists.length === 0}
        >
          + New Project
        </button>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading ? (
        <Spinner />
      ) : projects.length === 0 ? (
        <Empty>No projects yet.</Empty>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Artist</th>
                <th>Genre / theme</th>
                <th>Tribute</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 600 }}>{p.name}</td>
                  <td className="muted">{p.artists?.name ?? "—"}</td>
                  <td className="muted">{p.genre_theme ?? "—"}</td>
                  <td className="muted">{p.tribute_of ?? "—"}</td>
                  <td>
                    <div className="row-actions">
                      <button className="btn btn-sm btn-ghost" onClick={() => setEditing(p)}>
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          if (confirm(`Delete ${p.name}? Tracks keep existing but lose this link.`))
                            remove.mutate(p.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ProjectForm project={null} artists={artists} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <ProjectForm project={editing} artists={artists} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
